import { setToken } from '@/api_client/client';
import { useSearchParams } from '@/hooks/useSearchParams';
import consentRepository from '@/repositories/consent';
import googleRepository from '@/repositories/google.repository';
import { sendMessageToParentWindow } from '@/services/google-oauth.service';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export function OauthRedirectPage() {
	const search = useSearchParams();
	const [countdown, setCountdown] = useState<number | null>(null);
	const [hasProcessed, setHasProcessed] = useState(false);
	const { t } = useTranslation();

	const authCode = search.get('code');
	const stateBase64 = search.get('state');

	const throwError = (message: string) => {
		sendMessageToParentWindow({ message, status: 'FAILED' });
		setCountdown(5);
	};

	// Countdown effect
	useEffect(() => {
		if (countdown !== null && countdown > 0) {
			const timer = setTimeout(() => {
				setCountdown(prev => prev !== null ? prev - 1 : null);
			}, 1000);
			return () => clearTimeout(timer);
		} else if (countdown === 0) {
			window.close();
		}
	}, [countdown]);

	// OAuth processing effect - only runs once
	useEffect(() => {
		if (hasProcessed) return;

		if (!authCode || !stateBase64) {
			throwError('Invalid URL');
			setHasProcessed(true);
			return;
		}

		const decodedState = atob(stateBase64!);
		const state = JSON.parse(decodedState);

		if (!state.authToken) {
			throwError('Missing token in state');
			setHasProcessed(true);
			return;
		}

		if (!state.provider) {
			throwError('Missing provider in state');
			setHasProcessed(true);
			return;
		}

		setToken(state.authToken);

		// Handle different OAuth providers
		if (state.provider === 'GOOGLE') {
			if (!state.redirectUri) {
				throwError('Missing redirectUri in state');
				setHasProcessed(true);
				return;
			}

			googleRepository
				.consent(authCode!, state.redirectUri)
				.then((res) => res.data)
				.then(() => {
					sendMessageToParentWindow({ status: 'SUCCESS' });
					setCountdown(5);
					setHasProcessed(true);
				})
				.catch(() => {
					sendMessageToParentWindow({ status: 'FAILED', message: 'SERVER_ERROR' });
					setCountdown(5);
					setHasProcessed(true);
				});
		} else if (state.provider === 'MICROSOFT') {
			if (!state.redirectUri) {
				throwError('Missing redirectUri in state');
				setHasProcessed(true);
				return;
			}

			consentRepository
				.consent({
					authCode: authCode!,
					provider: 'MICROSOFT',
					redirectUri: state.redirectUri
				})
				.then(() => {
					sendMessageToParentWindow({ status: 'SUCCESS' });
					setCountdown(5);
					setHasProcessed(true);
				})
				.catch(() => {
					sendMessageToParentWindow({ status: 'FAILED', message: 'SERVER_ERROR' });
					setCountdown(5);
					setHasProcessed(true);
				});
		} else {
			throwError('Unsupported provider');
			setHasProcessed(true);
		}
	}, [authCode, stateBase64, hasProcessed]);

	return (
		<div style={{ 
			display: 'flex', 
			flexDirection: 'column', 
			alignItems: 'center', 
			justifyContent: 'center', 
			height: '100vh', 
			fontFamily: 'Arial, sans-serif',
			textAlign: 'center',
			padding: '20px'
		}}>
			{countdown !== null ? (
				<>
					<h2>{t('Process completed!')}</h2>
					<p>{t('This tab will close automatically in {{count}} seconds.', { count: countdown })}</p>
					<p style={{ fontSize: '14px', color: '#666' }}>
						{t('You can close it manually if you prefer.')}
					</p>
				</>
			) : (
				<div>
					<h2>{t('Processing...')}</h2>
					<p>{t('Please wait while we complete the setup.')}</p>
				</div>
			)}
		</div>
	);
}
